import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'src/Lca/components/Breadcrumbs';
import LcaLayout from 'src/Lca/components/layout';
import { Subtitle, Title } from 'src/Lca/components/Texts';
import Loader from 'src/components/Loader';
import colors from 'src/theme/colors';
import { useLcaProduct } from 'src/Lca/hooks';
import ProductPassportBuilder from '../Builder';

interface ProductPassportShareProps {
  passportToken?: string;
  isEmbedded?: boolean;
  productId?: number;
}

export default function ProductPassportShare({
  passportToken,
  isEmbedded = false,
  productId,
}: ProductPassportShareProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'product.sharePage' });
  const params = useParams();
  const resolvedPassportToken = passportToken || params.passportToken;

  if (!resolvedPassportToken) {
    return null;
  }

  return (
    <Box
      sx={{
        maxWidth: 1600,
        mx: 'auto',
        mt: isEmbedded ? 2 : 4,
        mb: 6,
        px: 2,
      }}
    >
      <Typography variant="h4" color={colors.darkGrey} mb={2}>
        {t('title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        {t('description')}
      </Typography>

      <ProductPassportBuilder
        passportToken={resolvedPassportToken}
        productId={productId}
      />
    </Box>
  );
}

export function ProductPassportSharePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('lca');
  const productId = Number(id);
  const { product, isLoading } = useLcaProduct(productId);

  if (isLoading) {
    return <Loader />;
  }

  if (!product) {
    return null;
  }

  const breadcrumbs = [
    { name: t('productList'), url: '/lca/products' },
    { name: product.name, url: `/lca/products/${productId}` },
    {
      name: t('product.sharePassport'),
      url: `/lca/products/${productId}/share-product-passport`,
    },
  ];

  return (
    <LcaLayout>
      <Breadcrumbs data={breadcrumbs} />
      <Subtitle text={t('product.sharePassport')} />
      <div style={{ marginTop: '44px', marginBottom: '40px' }}>
        <Title text={product.name} />
      </div>
      <ProductPassportShare
        passportToken={product.passport_access_token}
        productId={productId}
      />
    </LcaLayout>
  );
}
